import React from 'react';
import {
  Activity,
  FlaskConical,
  Pill,
  Bug,
  Fingerprint,
  Layers,
  Slice,
  Ear,
  ScanLine,
  Ribbon,
  Bone,
  Baby,
  Brain,
  Eye,
  Syringe,
  Stethoscope,
  Microscope,
  Heart,
  Users,
} from 'lucide-react';

// ---------- Custom SVG icons (drop-in replacements for lucide-react) ----------
// Each accepts `size` and `className` so it can be dropped anywhere a lucide
// icon is used. Follows the lucide look: 24x24 grid, stroke-width 2, currentColor.
const Svg = ({ children, size = 24, className = '', ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    {children}
  </svg>
);

export const Tooth = (props) => (
  <Svg {...props}>
    <path d="M8.5 3C6.5 3 5 4.5 5 6.5c0 2 .5 3.5 1 5.5.4 1.6.8 3.4 1 5 .2 1.3.5 3 1.7 3 1.1 0 1.3-1.2 1.5-2.5.2-1.4.4-3 1.8-3s1.6 1.6 1.8 3c.2 1.3.4 2.5 1.5 2.5 1.2 0 1.5-1.7 1.7-3 .2-1.6.6-3.4 1-5 .5-2 1-3.5 1-5.5C19 4.5 17.5 3 15.5 3c-1.2 0-2 .8-3.5.8S9.7 3 8.5 3z" />
  </Svg>
);

export const Uterus = (props) => (
  <Svg {...props}>
    <path d="M7 3v3" />
    <path d="M17 3v3" />
    <path d="M7 6c-1.4 2.6-3 4.8-3 8a8 8 0 0 0 16 0c0-3.2-1.6-5.4-3-8" />
    <path d="M9 15c1.2 1.6 3.8 1.6 6 0" />
  </Svg>
);

export const Kidneys = (props) => (
  <Svg {...props}>
    <path d="M8 6c-2.5.5-4 2.5-4 6s1.5 5.5 4 6c2 .4 3-1 3-3v-6c0-2-1-3.4-3-3z" />
    <path d="M16 6c2.5.5 4 2.5 4 6s-1.5 5.5-4 6c-2 .4-3-1-3-3v-6c0-2 1-3.4 3-3z" />
  </Svg>
);

export const Lungs = (props) => (
  <Svg {...props}>
    <path d="M12 4v11" />
    <path d="M9 4h6" />
    <path d="M12 15c-1 3-3 5-5 5-1.7 0-3-1.3-3-3 0-2.5.8-5 2-7 1.1-1.8 2.4-3 4-3 1.2 0 2 .8 2 2" />
    <path d="M12 15c1 3 3 5 5 5 1.7 0 3-1.3 3-3 0-2.5-.8-5-2-7-1.1-1.8-2.4-3-4-3-1.2 0-2 .8-2 2" />
  </Svg>
);

// ---------- Department name → icon map ----------
// Each rule's `match` is a regex source. It's compiled with a leading `\b`
// (word start) so short keywords don't accidentally match longer names —
// e.g. `urology` no longer matches "Neurology". First matching rule wins;
// order from most-specific to most-generic.
const RULES = [
  // ↓ PDF corrections
  { match: 'physiology', icon: Activity },
  { match: 'biochemistry', icon: FlaskConical },
  { match: 'pharmacology', icon: Pill },
  { match: 'microbiology', icon: Bug },
  { match: 'forensic', icon: Fingerprint },
  { match: 'dermatology', icon: Layers },
  { match: 'general surgery', icon: Slice },
  { match: 'oto-rhino', icon: Ear },
  { match: 'ent', icon: Ear },
  { match: 'obst', icon: Uterus },
  { match: 'gyn', icon: Uterus },
  { match: 'radio', icon: ScanLine },
  { match: 'dent', icon: Tooth },
  { match: 'respiratory', icon: Lungs },
  { match: 'oncology', icon: Ribbon },
  { match: 'cancer', icon: Ribbon },
  // ↓ PDF explicitly flagged as fine (unchanged from previous mapping).
  // `neurology` intentionally placed BEFORE `urology` so the word-boundary
  // regex resolves correctly.
  { match: 'neurology', icon: Brain },
  { match: 'urology', icon: Kidneys },
  { match: 'anatomy', icon: Bone },
  { match: 'orthopaedic', icon: Bone },
  { match: 'orthopedic', icon: Bone },
  { match: 'pathology', icon: Microscope },
  { match: 'community', icon: Users },
  { match: 'general medicine', icon: Stethoscope },
  { match: 'paediatric surgery', icon: Baby },
  { match: 'pediatric surgery', icon: Baby },
  { match: 'paediatric', icon: Baby },
  { match: 'pediatric', icon: Baby },
  { match: 'psychiatry', icon: Brain },
  { match: 'ophthalmology', icon: Eye },
  { match: 'anaesth', icon: Syringe },
  { match: 'anesth', icon: Syringe },
  { match: 'cardiology', icon: Heart },
];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const COMPILED = RULES.map((r) => ({
  icon: r.icon,
  re: new RegExp(`\\b${escapeRegExp(r.match)}`, 'i'),
}));

export const getDeptIcon = (name) => {
  const key = name || '';
  const found = COMPILED.find((r) => r.re.test(key));
  return found ? found.icon : Stethoscope;
};
